/*
  New product page (PDP redesign). Loaded only when the new page is active:
  Theme settings > New product page > "Use new product page", or ?view=redesign.
  Rendered by snippets/pf-pdp.liquid, which also outputs the #pf-pdp-data JSON this file reads.

  Product details, the offer card, pay-later, Popular collections and the USP strip are rendered in Liquid.
  This file adds the behaviour on top:
    - Gallery: mobile swipe dots, full-screen viewer with zoom, "See it in action" video
    - Add to cart + round wishlist heart on one row (WishlistHero injects its button with JS)
    - Sold-out state (Klaviyo "Notify me" as the main action + short note)
    - Product details: smooth open/close, one row open at a time, live delivery countdown
    - Offer card and rail cards: Add to cart (refreshes and opens the cart drawer)
    - Recently viewed rail (only with Preferences consent) + More like this rail
    - Popular collections slow auto-glide
*/
(function () {
  const dataEl = document.getElementById('pf-pdp-data');
  if (!dataEl) return;
  let PF;
  try {
    PF = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }
  const settings = PF.settings || {};
  const current = PF.product || {};

  // ---------- Shared helpers ----------
  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const decode = (s) => {
    const t = document.createElement('textarea');
    t.innerHTML = s ?? '';
    return t.value;
  };
  const money = (pence) => '£' + (pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // /products/x.js prices are pence (numbers); suggest.json and products.json prices are pounds ("6.00").
  const toPence = (v) => (v == null || v === '' ? null : typeof v === 'string' ? Math.round(parseFloat(v) * 100) : +v);
  const sized = (src, width) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${width}` : '');
  const json = (url) =>
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const info = document.querySelector('.product__info-container');

  // Same drawer refresh as assets/pf-options-popover.js.
  function refreshDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      window.location.href = '/cart';
      return;
    }
    fetch('/cart?section_id=cart-drawer')
      .then((r) => r.text())
      .then((html) => {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
        const drawerInner = document.querySelector('#CartDrawer');
        if (fresh && drawerInner) drawerInner.innerHTML = fresh.innerHTML;
        drawer.classList.remove('is-empty');
        document.dispatchEvent(new Event('updateShippingBar'));
        document.dispatchEvent(new Event('updatePromoBar'));
        document.dispatchEvent(new Event('pf:cart-changed'));
        if (typeof drawer.open === 'function') drawer.open();
      })
      .catch(() => {});
  }

  // Adds one of a variant, then shows "Added" (teal) for 2.5s.
  function addToCart(btn, variantId, addedClass) {
    if (btn.disabled) return;
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: +variantId, quantity: 1 }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(() => {
        btn.classList.add(addedClass);
        btn.textContent = 'Added';
        refreshDrawer();
        setTimeout(() => {
          btn.classList.remove(addedClass);
          btn.textContent = label;
          btn.disabled = false;
        }, 2500);
      })
      .catch(() => {
        btn.disabled = false;
        btn.textContent = 'Try again';
      });
  }

  // ======================================================================
  // Product rail + card (Recently viewed, More like this)
  // ======================================================================
  const Rail = (function () {
    const icons = {
      left: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>',
      right: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>',
    };

    function normalize(p) {
      const variants = p.variants || [];
      const variant = variants.find((v) => v.available) || variants[0] || {};
      const image = typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image && p.featured_image.url) || p.image || '';
      const price = toPence(variant.price ?? p.price);
      const compareAt = toPence(variant.compare_at_price ?? p.compare_at_price_max ?? p.compare_at_price);
      return {
        url: (p.url || `/products/${p.handle}`).split('?')[0],
        title: decode(p.title),
        image,
        price,
        compareAt: compareAt && compareAt > price ? compareAt : null,
        available: !!p.available,
        variantId: variant.id,
        hasOptions: variants.length > 1,
      };
    }

    function card(p) {
      const save = p.compareAt ? Math.floor(((p.compareAt - p.price) / p.compareAt) * 100) : 0;
      const img = p.image
        ? `<img class="pf-card__img" src="${esc(sized(p.image, 400))}" alt="${esc(p.title)}" width="400" height="400" loading="lazy">`
        : '<span class="pf-card__img pf-card__img--empty"></span>';

      let action = `<button type="button" class="pf-card__action" data-pf-add="${p.variantId}">Add to cart</button>`;
      if (!p.available) action = '<span class="pf-card__action pf-card__action--disabled">Sold out</span>';
      else if (p.hasOptions) action = `<a class="pf-card__action" href="${esc(p.url)}">Choose options</a>`;

      return `
        <article class="pf-card">
          <a class="pf-card__media" href="${esc(p.url)}" tabindex="-1" aria-hidden="true">
            ${img}
            ${save >= 5 ? `<span class="pf-card__badge">Save ${save}%</span>` : ''}
          </a>
          <div class="pf-card__body">
            <h3 class="pf-card__title"><a href="${esc(p.url)}">${esc(p.title)}</a></h3>
            <p class="pf-card__price">
              <span class="pf-card__now">${money(p.price)}</span>
              ${p.compareAt ? `<s class="pf-card__was"><span class="visually-hidden">RRP </span>${money(p.compareAt)}</s>` : ''}
            </p>
            ${action}
          </div>
        </article>`;
    }

    function wire(section) {
      const track = section.querySelector('.pf-rail__track');
      const [prev, next] = section.querySelectorAll('.pf-rail__arrow');
      const thumb = section.querySelector('.pf-rail__thumb');

      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        section.classList.toggle('pf-rail--static', max <= 2);
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max - 2;
        const visiblePct = Math.max((track.clientWidth / track.scrollWidth) * 100, 12);
        const fraction = max > 0 ? track.scrollLeft / max : 0;
        thumb.style.width = `${visiblePct}%`;
        thumb.style.transform = `translateX(${(fraction * (100 - visiblePct) * 100) / visiblePct}%)`;
      };

      // Scroll by however many whole cards are visible (scrolls the track only, never the page).
      const step = () => {
        const slide = track.querySelector('.pf-rail__slide');
        if (!slide) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const size = slide.getBoundingClientRect().width + gap;
        return size * Math.max(1, Math.floor((track.clientWidth + gap) / size));
      };

      prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      section.querySelectorAll('img').forEach((img) => img.addEventListener('load', update, { once: true }));
      section.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-pf-add]');
        if (btn) addToCart(btn, btn.dataset.pfAdd, 'pf-card__action--added');
      });
      update();
    }

    let uid = 0;
    function mount(container, { title, products, position = 'beforeend', className = '', actionsHtml = '' }) {
      const id = `pf-rail-${++uid}`;
      const section = document.createElement('section');
      section.className = `pf-rail ${className}`.trim();
      section.setAttribute('aria-labelledby', `${id}-title`);
      section.innerHTML = `
        <div class="page-width">
          <div class="pf-rail__head">
            <h2 class="pf-rail__title" id="${id}-title">${esc(title)}</h2>
            <div class="pf-rail__tools">
              ${actionsHtml}
              <div class="pf-rail__nav">
                <button type="button" class="pf-rail__arrow" aria-label="Previous products" aria-controls="${id}-track">${icons.left}</button>
                <button type="button" class="pf-rail__arrow" aria-label="Next products" aria-controls="${id}-track">${icons.right}</button>
              </div>
            </div>
          </div>
          <ul class="pf-rail__track" id="${id}-track" role="list">
            ${products.map((p) => `<li class="pf-rail__slide">${card(p)}</li>`).join('')}
          </ul>
          <div class="pf-rail__progress" aria-hidden="true"><span class="pf-rail__thumb"></span></div>
        </div>`;
      container.insertAdjacentElement(position, section);
      wire(section);
      return section;
    }

    return { mount, normalize };
  })();

  // Rails live in the "Product rails" section (sections/pf-product-rails.liquid).
  // Recently viewed always goes first, More like this after it.
  const railsContainer = document.querySelector('[data-pf-rails]');

  // ======================================================================
  // Recently viewed
  // Consent (PECR): browsing-history personalisation needs opt-in, so nothing is read, written or shown unless
  // the shopper allowed "Preferences" in the Exceedity cookie banner (`_excons` cookie, same check as the shim in
  // layout/theme.liquid). No cookie / no decision = not allowed. Accepting mid-visit starts recording without a
  // reload; turning Preferences off deletes the history and removes the rail.
  // ======================================================================
  (function () {
    const KEY = 'pf_recently_viewed';
    const MAX = 20;
    const handle = current.handle;

    // Exceedity CMP consent cookie: base64url JSON { v: 1, purposes: { necessary, preferences, analytics, marketing } }.
    const preferencesAllowed = () => {
      try {
        const raw = document.cookie.split('; ').find((c) => c.startsWith('_excons='));
        if (!raw) return false;
        const value = raw.slice('_excons='.length);
        const decoded = atob(value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4));
        const consent = JSON.parse(decoded);
        return !!consent && consent.v === 1 && !!consent.purposes && consent.purposes.necessary === true && consent.purposes.preferences === true;
      } catch (e) {
        return false;
      }
    };

    const read = () => {
      try {
        return JSON.parse(localStorage.getItem(KEY)) || [];
      } catch (e) {
        return [];
      }
    };
    const write = (list) => {
      try {
        localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
      } catch (e) {
        /* storage unavailable (private mode etc.) - rail just won't show */
      }
    };
    const forget = () => {
      try {
        localStorage.removeItem(KEY);
      } catch (e) {
        /* nothing to remove */
      }
      document.querySelectorAll('.pf-rail--recent').forEach((el) => el.remove());
    };

    let started = false;
    const start = () => {
      if (started || !handle) return;
      started = true;

      const history = read();
      write([{ handle, t: Date.now() }, ...history.filter((item) => item.handle !== handle)]);

      if (!railsContainer) return;
      const handles = history.map((item) => item.handle).filter((h) => h && h !== handle).slice(0, 12);
      if (!handles.length) return;

      Promise.all(handles.map((h) => json(`/products/${h}.js`))).then((products) => {
        const items = products.filter((p) => p && p.type !== 'Wholesale').map(Rail.normalize);
        // Consent may have been withdrawn while the products were loading.
        if (!items.length || !preferencesAllowed()) return;

        const section = Rail.mount(railsContainer, {
          title: 'Recently viewed',
          products: items,
          position: 'afterbegin',
          className: 'pf-rail--recent',
          actionsHtml: '<button type="button" class="pf-rail__link" data-pf-rv-clear>Clear</button>',
        });
        section.querySelector('[data-pf-rv-clear]').addEventListener('click', () => {
          write([{ handle, t: Date.now() }]);
          section.remove();
        });
      });
    };

    // Banner decisions made during this visit (accept, reject, or changing Preferences later).
    window.addEventListener('consent:change', (event) => {
      const purposes = (event.detail && event.detail.purposes) || {};
      if (purposes.preferences === true) {
        start();
      } else {
        started = false;
        forget();
      }
    });

    if (preferencesAllowed()) start();
    else forget(); // No consent (or withdrawn on a previous visit): make sure nothing is left behind.
  })();

  // ======================================================================
  // More like this: "More from <brand>" / "More <theme>" / "More like this"
  // Candidates come from brand search, the product's 3 most specific theme collections, Shopify's "related"
  // recommendations and same product type. Each candidate is scored against this product:
  //   Same brand (vendor)                                  +50
  //   Shares the product's most specific theme collection  +35  (another of its top themes: +20)
  //   Each extra theme collection shared                   +5   (up to +15)
  //   Shopify "related" recommendation                     +20 down to +8 by rank
  //   Similar price (half to double this price)            +10  (under a third or over 3x: -20)
  //   Same product type                                    +5
  // Theme collections = the product's smallest collections (from product.collections in Liquid), ignoring broad
  // ones (age, price, sale, events, multi-buy, wholesale, bundles, over 400 products) and occasion ones
  // (Theme settings > New product page). At most 2 cards per product line. Shows up to 10, hidden if fewer than 2.
  // ======================================================================
  (function () {
    if (!railsContainer || !current.handle) return;

    const handle = current.handle;
    const generic = (settings.genericVendors || []).filter(Boolean).map((v) => v.toLowerCase());
    const ignoreThemes = (settings.ignoreThemeCollections || []).filter(Boolean);
    const maxPerLine = settings.maxPerProductLine || 2;

    // Broad / non-theme collections (same rules the prototype's collection map used).
    const BROAD =
      /^(toys-for-|educational-toys-for-|wholesale-|cheap-wholesale|stocking-fillers|gifts-for-|app-)|black-friday|cyber-monday|boxing-day|back-in-stock|toy-deals|secret-toy|deals|^\d+-for-|3for2|sale|clearance|under-|penny|perfect-for-ptas|pf-retail|trending|bundles|^all$|frontpage|^new|best-?sell|easter|eid/i;

    const suggest = (query) =>
      json(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=hide`
      ).then((d) => (d && d.resources && d.resources.results && d.resources.results.products) || []);
    const collectionProducts = (h, limit) => json(`/collections/${h}/products.json?limit=${limit}`).then((d) => (d && d.products) || []);

    const isWholesale = (p) => p.type === 'Wholesale' || p.product_type === 'Wholesale' || String(p.handle).startsWith('wholesale-');
    const priceOf = (p) => toPence((p.variants && p.variants[0] && p.variants[0].price) ?? p.price);
    const availableOf = (p) => (p.available != null ? !!p.available : (p.variants || []).some((v) => v.available));

    const productLine = (title) =>
      String(title)
        .toLowerCase()
        .replace(/&amp;/g, '&')
        .replace(/[^a-z0-9 ]+/g, ' ')
        .split(/\s+/)
        .filter((w) => w && w !== 'the')
        .slice(0, 3)
        .join(' ');

    const niceTheme = (title) =>
      String(title)
        .replace(/^cheap\s+/i, '')
        .replace(/\s+for\s+(kids|children|girls and boys|boys and girls)$/i, '')
        .trim();

    (async () => {
      const vendor = (current.vendor || '').trim();
      const brandSearch = vendor && !generic.includes(vendor.toLowerCase());
      const price = current.price;

      const themes = (PF.collections || [])
        .filter((c) => !BROAD.test(c.handle) && c.count >= 4 && c.count <= 400)
        .filter((c) => !ignoreThemes.some((word) => c.handle.includes(word)))
        .sort((a, b) => a.count - b.count)
        .slice(0, 3);
      const themeHandles = themes.map((t) => t.handle);

      const [brandList, relatedData, typeList, ...themeLists] = await Promise.all([
        brandSearch ? suggest(`vendor:"${vendor}"`) : [],
        json(`/recommendations/products.json?product_id=${current.id}&limit=16&intent=related`),
        current.type ? suggest(`product_type:"${current.type}"`) : [],
        ...themes.map((t, i) => collectionProducts(t.handle, i === 0 ? 40 : 20)),
      ]);
      const related = (relatedData && relatedData.products) || [];

      // Which of this product's theme collections each candidate is in (from the collection lists above).
      const membership = new Map();
      themeLists.forEach((list, i) =>
        list.forEach((p) => membership.set(p.handle, [...(membership.get(p.handle) || []), themeHandles[i]]))
      );

      const candidates = new Map();
      const add = (p) => {
        if (!p || p.handle === handle || isWholesale(p) || candidates.has(p.handle)) return;
        candidates.set(p.handle, p);
      };
      brandList.forEach(add);
      themeLists.flat().forEach(add);
      related.forEach(add);
      typeList.forEach(add);

      const relatedRank = new Map(related.map((p, i) => [p.handle, i]));

      const scored = [];
      candidates.forEach((p, h) => {
        if (!availableOf(p)) return;
        const reasons = [];
        let score = 0;

        if (brandSearch && String(p.vendor || '').trim().toLowerCase() === vendor.toLowerCase()) {
          score += 50;
          reasons.push('brand');
        }

        const shared = membership.get(h) || [];
        if (shared.includes(themeHandles[0])) score += 35;
        else if (shared.length) score += 20;
        score += Math.min(15, Math.max(0, shared.length - 1) * 5);
        shared.forEach((t) => reasons.push(`theme:${t}`));

        if (relatedRank.has(h)) {
          score += Math.max(8, 20 - relatedRank.get(h));
          reasons.push('related');
        }

        const candidatePrice = priceOf(p);
        if (candidatePrice && price) {
          const ratio = candidatePrice / price;
          if (ratio >= 0.5 && ratio <= 2) score += 10;
          else if (ratio < 1 / 3 || ratio > 3) score -= 20;
        }

        if (current.type && (p.type || p.product_type) === current.type) score += 5;

        scored.push({ handle: h, title: p.title, score, reasons, priceGap: candidatePrice ? Math.abs(candidatePrice - price) : Infinity });
      });

      scored.sort((a, b) => b.score - a.score || a.priceGap - b.priceGap);

      // Variety: cap cards from the same product line, before loading full data.
      const lineCounts = new Map();
      const varied = scored.filter((s) => {
        if (s.score <= 0) return false;
        const line = productLine(s.title);
        const count = lineCounts.get(line) || 0;
        if (count >= maxPerLine) return false;
        lineCounts.set(line, count + 1);
        return true;
      });

      // Load full product data for the best matches (variant IDs + live stock for Add to cart).
      const top = varied.slice(0, 14);
      const full = await Promise.all(top.map((s) => json(`/products/${s.handle}.js`)));
      const picks = [];
      full.forEach((p, i) => {
        if (p && p.available && picks.length < 10) picks.push({ product: p, reasons: top[i].reasons });
      });
      if (picks.length < 2) return;

      // Title from what was actually picked.
      const half = Math.ceil(picks.length / 2);
      const brandCount = picks.filter((x) => x.reasons.includes('brand')).length;
      const bestTheme = themes
        .map((t) => ({ theme: t, count: picks.filter((x) => x.reasons.includes(`theme:${t.handle}`)).length }))
        .sort((a, b) => b.count - a.count || a.theme.count - b.theme.count)[0];
      let title = 'More like this';
      if (brandSearch && brandCount >= half) title = `More from ${vendor}`;
      else if (bestTheme && bestTheme.count >= half) title = `More ${niceTheme(bestTheme.theme.title)}`;

      Rail.mount(railsContainer, {
        title,
        products: picks.map((x) => Rail.normalize(x.product)),
        position: 'beforeend',
        className: 'pf-rail--more',
      });
    })();
  })();

  // ======================================================================
  // Gallery: mobile swipe dots, full-screen viewer (zoom, swipe, thumbnails), "See it in action" video
  // ======================================================================
  (function () {
    const viewer = document.querySelector('slider-component[id^="GalleryViewer"]');
    const list = viewer && viewer.querySelector('.product__media-list');
    if (!viewer || !list) return;

    const slides = Array.from(list.querySelectorAll('.product__media-item'));
    if (!slides.length) return;

    const bigSrc = (src) =>
      src ? (/[?&]width=\d+/.test(src) ? src.replace(/width=\d+/, 'width=2000') : `${src}${src.includes('?') ? '&' : '?'}width=2000`) : '';
    const smallSrc = (src) => (src ? src.replace(/width=\d+/, 'width=160') : '');

    const icon = {
      expand: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>',
      close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
      prev: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>',
      next: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>',
      play: '<svg viewBox="0 0 10 12" fill="currentColor" aria-hidden="true"><path d="M0 0l10 6-10 6z"/></svg>',
    };

    // ---------- Media list ----------
    // One entry per gallery slide. Slides holding a video uploaded to the product (current.videos, matched on the
    // slide's data-media-id "<section>-<media id>") play that video in the viewer instead of showing its poster.
    const uploaded = new Map((current.videos || []).map((v) => [String(v.id), v]));
    const videoSrc = (v) => {
      const mp4s = (v.sources || []).filter((s) => s.url).sort((a, b) => a.height - b.height);
      const best = mp4s.filter((s) => s.height <= 1080).pop() || mp4s[0];
      return best ? best.url : '';
    };
    const slideMedia = slides.map((slide) => {
      const img = slide.querySelector('img');
      const video = uploaded.get((slide.dataset.mediaId || '').split('-').pop());
      const src = video && videoSrc(video);
      if (src) return { type: 'video', src, poster: video.poster || '' };
      return img ? { type: 'image', src: bigSrc(img.getAttribute('src')), thumb: smallSrc(img.getAttribute('src')), alt: img.getAttribute('alt') || '' } : null;
    });
    const mediaIndex = (slideIndex) => Math.max(0, media.indexOf(slideMedia[slideIndex]));

    // YouTube ID from the product video field (custom.youtube_embed_code: YouTube embed code, or the old
    // custom.product_video link). Anything that is not YouTube is ignored.
    const youtubeId = (url) => {
      const m = String(url || '').match(/(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/))([\w-]{11})/);
      return m ? m[1] : null;
    };
    const youtube = youtubeId(current.videoUrl);
    const media = slideMedia.filter(Boolean);
    if (youtube) media.push({ type: 'video', youtube });
    const images = media.filter((m) => m.type === 'image');
    // "See it in action": the YouTube video, else the first uploaded video. Neither = no button.
    const videoIndex = youtube ? media.length - 1 : media.findIndex((m) => m.type === 'video');

    // ---------- On-page: expand button, video button, mobile dots ----------
    const expandBtn = document.createElement('button');
    expandBtn.type = 'button';
    expandBtn.className = 'pf-gal-expand';
    expandBtn.setAttribute('aria-label', 'View images full screen');
    expandBtn.innerHTML = icon.expand;
    viewer.appendChild(expandBtn);

    if (videoIndex > -1) {
      const videoBtn = document.createElement('button');
      videoBtn.type = 'button';
      videoBtn.className = 'pf-gal-video';
      videoBtn.innerHTML = `<span class="pf-gal-video__play">${icon.play}</span><span>See it in action</span>`;
      videoBtn.addEventListener('click', () => open(videoIndex, videoBtn));
      viewer.appendChild(videoBtn);
    }

    const dots = document.createElement('div');
    dots.className = 'pf-gal-dots';
    dots.setAttribute('role', 'group');
    dots.setAttribute('aria-label', 'Product images');
    dots.hidden = slides.length < 2;
    dots.innerHTML = slides
      .map((_, i) => `<button type="button" class="pf-gal-dot" aria-label="Image ${i + 1} of ${slides.length}"${i === 0 ? ' aria-current="true"' : ''}></button>`)
      .join('');
    viewer.insertAdjacentElement('afterend', dots);

    const pageIndex = () => {
      const offsets = slides.map((s) => s.offsetLeft - slides[0].offsetLeft);
      let best = 0;
      offsets.forEach((o, i) => {
        if (Math.abs(o - list.scrollLeft) < Math.abs(offsets[best] - list.scrollLeft)) best = i;
      });
      return best;
    };
    const syncDots = () => {
      const index = pageIndex();
      dots.querySelectorAll('.pf-gal-dot').forEach((d, i) => d.setAttribute('aria-current', String(i === index)));
    };
    list.addEventListener('scroll', () => requestAnimationFrame(syncDots), { passive: true });
    dots.addEventListener('click', (event) => {
      const dot = event.target.closest('.pf-gal-dot');
      if (!dot) return;
      const i = Array.from(dots.children).indexOf(dot);
      list.scrollTo({ left: slides[i].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
    });

    // Current image on desktop = the visible slide (Dawn hides the others); on mobile = scroll position.
    const currentImage = () => {
      const visible = slides.findIndex((s) => s.classList.contains('is-active') && s.getBoundingClientRect().width > 0);
      return window.matchMedia('(max-width: 749px)').matches ? pageIndex() : Math.max(0, visible);
    };

    expandBtn.addEventListener('click', () => open(mediaIndex(currentImage()), expandBtn));
    list.addEventListener('click', (event) => {
      const slide = event.target.closest('.product__media-item');
      if (!slide || event.target.closest('button, a')) return;
      event.preventDefault();
      open(mediaIndex(slides.indexOf(slide)), slide);
    });

    // ---------- Full-screen viewer ----------
    const lb = document.createElement('div');
    lb.className = 'pf-lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Product media');
    lb.innerHTML = `
      <div class="pf-lb__top">
        <span class="pf-lb__count" aria-live="polite"></span>
        <span class="pf-lb__hint">Tap to zoom</span>
        <button type="button" class="pf-lb__btn pf-lb__close" aria-label="Close">${icon.close}</button>
      </div>
      <div class="pf-lb__stage">
        <button type="button" class="pf-lb__btn pf-lb__arrow pf-lb__arrow--prev" aria-label="Previous">${icon.prev}</button>
        <ul class="pf-lb__track" role="list">
          ${media
            .map((m, i) =>
              m.type === 'image'
                ? `<li class="pf-lb__slide" data-index="${i}"><img class="pf-lb__img" data-src="${esc(m.src)}" alt="${esc(m.alt)}" draggable="false"></li>`
                : m.youtube
                  ? `<li class="pf-lb__slide pf-lb__slide--video" data-index="${i}"><div class="pf-lb__video" data-youtube="${esc(m.youtube)}"></div></li>`
                  : `<li class="pf-lb__slide pf-lb__slide--video" data-index="${i}"><div class="pf-lb__video pf-lb__video--file" data-src="${esc(m.src)}" data-poster="${esc(m.poster)}"></div></li>`
            )
            .join('')}
        </ul>
        <button type="button" class="pf-lb__btn pf-lb__arrow pf-lb__arrow--next" aria-label="Next">${icon.next}</button>
      </div>
      <div class="pf-lb__bottom">
        ${media
          .map((m, i) =>
            m.type === 'image'
              ? `<button type="button" class="pf-lb__thumb" data-go="${i}" aria-label="Image ${i + 1}"><img src="${esc(m.thumb)}" alt=""></button>`
              : `<button type="button" class="pf-lb__thumb pf-lb__thumb--video" data-go="${i}" aria-label="Video">${icon.play}</button>`
          )
          .join('')}
      </div>`;
    document.body.appendChild(lb);

    const track = lb.querySelector('.pf-lb__track');
    const lbSlides = Array.from(track.children);
    const count = lb.querySelector('.pf-lb__count');
    const hint = lb.querySelector('.pf-lb__hint');
    const prevBtn = lb.querySelector('.pf-lb__arrow--prev');
    const nextBtn = lb.querySelector('.pf-lb__arrow--next');
    let index = 0;
    let returnFocus = null;

    const loadSlide = (i) => {
      const slide = lbSlides[i];
      if (!slide) return;
      const img = slide.querySelector('img[data-src]');
      if (img && !img.src) img.src = img.dataset.src;
    };

    const stopVideos = () => {
      lb.querySelectorAll('.pf-lb__video').forEach((v) => (v.innerHTML = ''));
    };

    const startVideo = (i) => {
      const holder = lbSlides[i] && lbSlides[i].querySelector('.pf-lb__video');
      if (!holder || holder.firstChild) return;
      if (!holder.dataset.youtube) {
        // Uploaded video: native player. If the browser blocks autoplay with sound, start muted.
        const video = document.createElement('video');
        video.src = holder.dataset.src;
        if (holder.dataset.poster) video.poster = holder.dataset.poster;
        video.controls = true;
        video.playsInline = true;
        holder.appendChild(video);
        video.play().catch(() => {
          video.muted = true;
          video.play().catch(() => {});
        });
        return;
      }
      holder.innerHTML = `<iframe src="https://www.youtube-nocookie.com/embed/${encodeURIComponent(holder.dataset.youtube)}?autoplay=1&rel=0&playsinline=1" title="Product video" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>`;
    };

    function setIndex(i) {
      if (i === index && count.textContent) return;
      unzoom();
      index = i;
      const m = media[i];
      count.textContent = m.type === 'video' ? 'Video' : `${images.indexOf(m) + 1} / ${images.length}`;
      hint.textContent = m.type === 'video' ? '' : window.matchMedia('(hover: hover)').matches ? 'Click to zoom' : 'Tap to zoom';
      prevBtn.disabled = i === 0;
      nextBtn.disabled = i === media.length - 1;
      lb.querySelectorAll('.pf-lb__thumb').forEach((t) => t.setAttribute('aria-current', String(+t.dataset.go === i)));
      [i - 1, i, i + 1].forEach(loadSlide);
      if (m.type === 'video') startVideo(i);
      else stopVideos();
    }

    function go(i, smooth = true) {
      const target = Math.max(0, Math.min(media.length - 1, i));
      track.scrollTo({ left: target * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      setIndex(target);
    }

    track.addEventListener(
      'scroll',
      () =>
        requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / track.clientWidth);
          if (i !== index) setIndex(i);
        }),
      { passive: true }
    );

    function open(i, trigger) {
      returnFocus = trigger || document.activeElement;
      lb.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      index = -1;
      requestAnimationFrame(() => {
        go(i, false);
        lb.querySelector('.pf-lb__close').focus({ preventScroll: true });
      });
    }

    function close() {
      if (!lb.classList.contains('is-open')) return;
      unzoom();
      stopVideos();
      lb.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus({ preventScroll: true });
    }

    lb.querySelector('.pf-lb__close').addEventListener('click', close);
    prevBtn.addEventListener('click', () => go(index - 1));
    nextBtn.addEventListener('click', () => go(index + 1));
    lb.querySelector('.pf-lb__bottom').addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-go]');
      if (thumb) go(+thumb.dataset.go);
    });

    document.addEventListener('keydown', (event) => {
      if (!lb.classList.contains('is-open')) return;
      if (event.key === 'Escape') close();
      else if (event.key === 'ArrowLeft') go(index - 1);
      else if (event.key === 'ArrowRight') go(index + 1);
      else if (event.key === 'Tab') {
        // Keep keyboard focus inside the viewer.
        const focusable = Array.from(lb.querySelectorAll('button:not(:disabled), iframe, video'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    // ---------- Zoom (tap/click to toggle, drag or move to look around) ----------
    const ZOOM = 2.5;
    let zoomed = null;
    let pan = null;

    function zoomAt(img, clientX, clientY) {
      const rect = img.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 100;
      const y = ((clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${Math.max(0, Math.min(100, x))}% ${Math.max(0, Math.min(100, y))}%`;
      img.style.transform = `scale(${ZOOM})`;
      img.closest('.pf-lb__slide').classList.add('is-zoomed');
      lb.classList.add('is-zoomed');
      zoomed = img;
      hint.textContent = window.matchMedia('(hover: hover)').matches ? 'Click to zoom out' : 'Drag to look around, tap to zoom out';
    }

    function unzoom() {
      if (!zoomed) return;
      zoomed.style.transform = '';
      zoomed.closest('.pf-lb__slide').classList.remove('is-zoomed', 'is-panning');
      lb.classList.remove('is-zoomed');
      zoomed = null;
      pan = null;
      if (media[index] && media[index].type === 'image') {
        hint.textContent = window.matchMedia('(hover: hover)').matches ? 'Click to zoom' : 'Tap to zoom';
      }
    }

    track.addEventListener('pointerdown', (event) => {
      const img = event.target.closest('.pf-lb__img');
      if (!img) return;
      pan = { img, x: event.clientX, y: event.clientY, moved: false, origin: img.style.transformOrigin };
      if (zoomed === img) img.setPointerCapture(event.pointerId);
    });

    track.addEventListener('pointermove', (event) => {
      if (!zoomed) return;
      // Mouse: the zoomed image follows the cursor. Touch/pen drag: move the image around.
      if (event.pointerType === 'mouse' && !pan) {
        zoomAt(zoomed, event.clientX, event.clientY);
        return;
      }
      if (!pan || pan.img !== zoomed) return;
      const dx = event.clientX - pan.x;
      const dy = event.clientY - pan.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) pan.moved = true;
      if (!pan.moved) return;
      const rect = zoomed.getBoundingClientRect();
      const [ox, oy] = (pan.origin || '50% 50%').split(' ').map(parseFloat);
      const nx = Math.max(0, Math.min(100, ox - (dx / rect.width) * 100 * ZOOM));
      const ny = Math.max(0, Math.min(100, oy - (dy / rect.height) * 100 * ZOOM));
      zoomed.closest('.pf-lb__slide').classList.add('is-panning');
      zoomed.style.transformOrigin = `${nx}% ${ny}%`;
    });

    track.addEventListener('pointerup', (event) => {
      const img = event.target.closest('.pf-lb__img');
      const wasDrag = pan && pan.moved;
      if (zoomed) zoomed.closest('.pf-lb__slide').classList.remove('is-panning');
      pan = null;
      if (!img || wasDrag) return;
      if (zoomed === img) unzoom();
      else zoomAt(img, event.clientX, event.clientY);
    });

    // Tap on the white space around an image closes zoom first, then nothing else (no accidental closes).
    track.addEventListener('click', (event) => {
      if (!event.target.closest('.pf-lb__img') && zoomed) unzoom();
    });
  })();

  // ======================================================================
  // Add to cart + round wishlist heart on one row.
  // WishlistHero injects its button with JS, so it is moved into the row here. Re-applies if the theme
  // re-renders the form (e.g. after adding to the basket).
  // ======================================================================
  (function () {
    if (!info) return;

    function apply() {
      const atc = info.querySelector('#add-to-cart.product-form__buttons');
      const wish = info.querySelector('#wishlisthero-product-page-button-container');
      if (!atc || !wish) return;

      let row = atc.parentElement.classList.contains('pf-atc-row') ? atc.parentElement : null;
      if (!row) {
        row = document.createElement('div');
        row.className = 'pf-atc-row';
        atc.before(row);
        row.appendChild(atc);
      }
      if (wish.parentElement !== row) row.appendChild(wish);

      const btn = wish.querySelector('button');
      if (btn && btn.dataset.pfHeart !== '1') {
        btn.dataset.pfHeart = '1';
        btn.title = btn.getAttribute('aria-label') || 'Add to wishlist';
      }
    }

    apply();
    new MutationObserver(apply).observe(info, { childList: true, subtree: true });
  })();

  // ======================================================================
  // Sold-out state: Klaviyo's "Notify me" becomes the main action (bell icon, beside the wishlist heart),
  // with a short note and a link to More like this. Pay-later and the free gift message are hidden.
  // Klaviyo only injects its button on the live domain.
  // ======================================================================
  (function () {
    if (!info) return;

    const bell =
      '<svg class="pf-bis-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 01-3.4 0"/></svg>';

    const isSoldOut = () => {
      const btn = info.querySelector('#add-to-cart button[name="add"]');
      return !!(btn && btn.disabled);
    };

    function styleKlaviyoButton() {
      const bis = info.querySelector('#add-to-cart .klaviyo-bis-trigger');
      if (!bis || bis.dataset.pfStyled === '1') return;
      bis.dataset.pfStyled = '1';
      bis.classList.add('pf-bis');
      bis.innerHTML = `${bell}<span>Notify me when it's back</span>`;
    }

    function addNote() {
      if (info.querySelector('.pf-soldout-note')) return;
      const anchor = info.querySelector('.pf-atc-row') || info.querySelector('#add-to-cart');
      if (!anchor) return;
      const note = document.createElement('p');
      note.className = 'pf-soldout-note';
      note.innerHTML =
        '<span class="pf-soldout-note__dot" aria-hidden="true"></span><span class="pf-soldout-note__text">Sold out right now. We\'ll email you as soon as it\'s back. <a href="#" class="pf-soldout-note__link">See similar toys</a></span>';
      note.querySelector('a').addEventListener('click', (event) => {
        event.preventDefault();
        const rail = document.querySelector('.pf-rail--more') || document.querySelector('.pf-rail--recent');
        if (!rail) return;
        const header = document.querySelector('sticky-header, .section-header');
        const offset = header ? header.getBoundingClientRect().height + 12 : 12;
        window.scrollTo({ top: rail.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
      });
      anchor.after(note);
    }

    function apply() {
      const soldOut = isSoldOut();
      document.body.classList.toggle('pf-soldout', soldOut);
      if (!soldOut) {
        const note = info.querySelector('.pf-soldout-note');
        if (note) note.remove();
        return;
      }
      styleKlaviyoButton();
      // Added once and never re-positioned (two scripts moving each other would loop forever).
      addNote();
    }

    apply();
    new MutationObserver(apply).observe(info, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  })();

  // ======================================================================
  // Product details: smooth open/close, one row open at a time, live delivery countdown.
  // The card itself is rendered by snippets/pf-pdp-details.liquid.
  // ======================================================================
  (function () {
    const wrap = document.querySelector('.pf-dd');
    if (!wrap) return;

    // --- Delivery estimate (UK time, Mon-Fri, cutoff from Theme settings) ---
    function ukNow() {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(new Date());
      const get = (type) => +parts.find((p) => p.type === type).value;
      return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
    }

    function addWorkingDays(date, count) {
      const d = new Date(date);
      let added = 0;
      while (added < count) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) added++;
      }
      return d;
    }

    function deliveryEstimate() {
      const now = ukNow();
      const weekday = now.getDay() >= 1 && now.getDay() <= 5;
      const cutoff = settings.cutoffHour ?? 14;
      const beforeCutoff = weekday && now.getHours() < cutoff;
      const date = addWorkingDays(now, beforeCutoff ? 1 : 2);
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const isTomorrow = date.toDateString() === tomorrow.toDateString();
      const dateText = date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

      let message = `Order now for delivery on <strong>${dateText}</strong>`;
      if (beforeCutoff) {
        const minsLeft = cutoff * 60 - (now.getHours() * 60 + now.getMinutes());
        const wait = `${Math.floor(minsLeft / 60)}h ${minsLeft % 60}m`;
        message = `Order within <strong>${wait}</strong> for delivery <strong>${isTomorrow ? 'tomorrow, ' : ''}${dateText}</strong>`;
      }
      const hint = isTomorrow ? 'Tomorrow' : date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
      return { message, hint, isTomorrow };
    }

    const etaEl = wrap.querySelector('[data-pf-eta]');
    const etaHint = wrap.querySelector('[data-pf-eta-hint]');
    const updateEta = () => {
      const next = deliveryEstimate();
      if (etaEl) {
        etaEl.innerHTML = next.message;
        etaEl.hidden = false;
      }
      if (etaHint) {
        etaHint.textContent = next.hint;
        etaHint.classList.toggle('pf-dd__hint--good', next.isTomorrow);
      }
    };
    if (etaEl || etaHint) {
      updateEta();
      setInterval(updateEta, 60000);
    }

    // --- Smooth open/close. Native <details> behaviour is kept for reduced motion. ---
    const items = [...wrap.querySelectorAll('.pf-dd__item')];
    const canAnimate = (panel) => !reduceMotion.matches && typeof panel.animate === 'function';

    function close(item) {
      const panel = item.querySelector('.pf-dd__panel');
      if (!canAnimate(panel)) {
        item.open = false;
        return;
      }
      if (item.dataset.animating === 'closing') return;
      panel.getAnimations().forEach((a) => a.cancel()); // stop a half-finished opening
      item.dataset.animating = 'closing';
      const closing = panel.animate([{ height: `${panel.scrollHeight}px` }, { height: '0px' }], { duration: 200, easing: 'ease-in', fill: 'forwards' });
      closing.onfinish = () => {
        item.open = false;
        closing.cancel();
        delete item.dataset.animating;
      };
    }

    items.forEach((item) => {
      const summary = item.querySelector('.pf-dd__summary');
      const panel = item.querySelector('.pf-dd__panel');

      // Opening a row closes whichever other row is open (covers both the animated and native paths).
      item.addEventListener('toggle', () => {
        if (item.open) items.forEach((other) => other !== item && other.open && close(other));
      });

      summary.addEventListener('click', (event) => {
        if (!canAnimate(panel)) return;
        event.preventDefault();
        if (item.dataset.animating) return;

        if (!item.open) {
          item.dataset.animating = 'opening';
          item.open = true;
          panel.animate([{ height: '0px' }, { height: `${panel.scrollHeight}px` }], { duration: 240, easing: 'ease-out' }).onfinish = () =>
            delete item.dataset.animating;
        } else {
          close(item);
        }
      });
    });
  })();

  // ======================================================================
  // Offer card: Add to cart (card rendered by snippets/pf-pdp-offer.liquid).
  // ======================================================================
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-pf-offer-add]');
    if (btn) addToCart(btn, btn.dataset.pfOfferAdd, 'pf-offer__btn--added');
  });

  // ======================================================================
  // Popular collections: slow auto-glide (native scrolling underneath, so swiping always works).
  // To the end, short pause, back to the start. Pauses while the shopper touches, hovers or tabs into it,
  // resumes a few seconds after they stop. Off with "reduce motion".
  // ======================================================================
  (function () {
    const section = document.querySelector('.pf-pop');
    const track = section && section.querySelector('.pf-pop__track');
    if (!track || reduceMotion.matches) return;

    const SPEED = 28; // px per second
    const END_PAUSE = 1800; // ms at each end
    const RESUME_AFTER = 3000; // ms after the shopper stops interacting

    let direction = 1;
    let position = 0;
    let last = null;
    let holdUntil = 0;
    let interacting = false;
    let resumeTimer = null;
    let visible = false;

    const pause = () => {
      interacting = true;
      clearTimeout(resumeTimer);
    };
    const resumeSoon = () => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        interacting = false;
        position = track.scrollLeft; // carry on from wherever they swiped to
        last = null;
      }, RESUME_AFTER);
    };

    track.addEventListener('pointerdown', pause);
    track.addEventListener('touchstart', pause, { passive: true });
    track.addEventListener('touchend', resumeSoon, { passive: true });
    track.addEventListener('pointerup', resumeSoon);
    track.addEventListener('mouseenter', pause);
    track.addEventListener('mouseleave', resumeSoon);
    track.addEventListener(
      'wheel',
      () => {
        pause();
        resumeSoon();
      },
      { passive: true }
    );
    section.addEventListener('focusin', pause);
    section.addEventListener('focusout', resumeSoon);

    // Only run while the section is on screen.
    new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      last = null;
    }).observe(track);

    function tick(now) {
      const max = track.scrollWidth - track.clientWidth;
      if (visible && !interacting && max > 1 && now >= holdUntil) {
        if (last != null) {
          position += direction * SPEED * ((now - last) / 1000);
          if (position >= max) {
            position = max;
            direction = -1;
            holdUntil = now + END_PAUSE;
          } else if (position <= 0) {
            position = 0;
            direction = 1;
            holdUntil = now + END_PAUSE;
          }
          track.scrollLeft = position;
        }
        last = now;
      } else {
        last = now >= holdUntil ? null : now;
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  })();
})();
